/**
 * PomodoroSettings · popup-above-widget settings panel · PR.4 full fill.
 * Per slot_pomodoro_rework PR.4 (17 MAY 2026 · Genie pick Q2 popup-above).
 *
 * Anchored к PomodoroWidget FAB (positioned bottom: 70 · right: 16 · above
 * 32px button + ~22px gap) · 300px width · opens upward. Z-index 43 · above
 * widget (42) · below reflection modal (70).
 *
 * Pattern: immediate-persist (NO draft state). Каждое изменение input
 * вызывает onChange(nextSettings) · hook commits immediately к localStorage и
 * Progress branch. «Сохранить» button = onClose (no separate commit · changes
 * already applied). «Закрыть» = onClose. «Сбросить настройки» = onResetDefaults.
 * «Сбросить таймер» = onResetTimer (preserved per `no_content_loss` rule ·
 * existing functionality from old Pomodoro.tsx).
 *
 * Dismissal:
 *   - Esc key
 *   - Click-outside (skips toggle button via data-pomo-settings-toggle attr)
 *   - Click «Закрыть»/«Сохранить» buttons
 *
 * Focus management: trap при open · returns focus к toggle button on close
 * (handled by widget consumer).
 */

import { useEffect, useRef } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { DEFAULT_POMO_SETTINGS, type PomoSettings } from '@/hooks/usePomodoro';

const Z_INDEX_POPUP = 43;

export interface PomodoroSettingsProps {
  /** Current settings · panel reflects this state · changes propagate via onChange. */
  settings: PomoSettings;
  /** Immediate-persist callback · fires per input change · no draft buffer. */
  onChange: (next: PomoSettings) => void;
  /** Restore settings к factory defaults (DEFAULT_POMO_SETTINGS). */
  onResetDefaults: () => void;
  /** Reset Pomodoro timer state к idle (preserves settings · cancels in-progress session). */
  onResetTimer: () => void;
  /** Close panel · changes already persisted. */
  onClose: () => void;
}

/** Validate numeric input · clamp в [min, max] · fallback к default при NaN. */
function clamp(value: number, min: number, max: number, fallback: number): number {
  if (Number.isNaN(value)) return fallback;
  return Math.max(min, Math.min(max, Math.round(value)));
}

export function PomodoroSettings({
  settings,
  onChange,
  onResetDefaults,
  onResetTimer,
  onClose,
}: PomodoroSettingsProps) {
  const cardRef = useFocusTrap<HTMLDivElement>(true);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Esc closes popup.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Click-outside dismissal · skips toggle button (data-pomo-settings-toggle).
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!panelRef.current) return;
      const target = e.target as Node | null;
      if (target && !panelRef.current.contains(target)) {
        const trigger = (target as HTMLElement).closest?.('[data-pomo-settings-toggle="true"]');
        if (!trigger) {
          onClose();
        }
      }
    };
    // Defer registration · opening click doesn't immediately close.
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [onClose]);

  // Immediate-persist field setter · partial update merged с current settings.
  const update = (patch: Partial<PomoSettings>) => {
    onChange({ ...settings, ...patch });
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="pomo-settings-title"
      data-testid="pomo-settings-panel"
      id="pomo-settings-popup"
      style={{
        position: 'fixed',
        bottom: 70,
        right: 16,
        zIndex: Z_INDEX_POPUP,
        width: 320,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 100px)',
        overflow: 'auto',
        background: 'var(--paper, #fffbf5)',
        border: '1.5px solid var(--ink, #1c1a18)',
        borderRadius: 6,
        boxShadow: '4px 4px 0 var(--peach, #F26B4A)',
        padding: '18px 20px',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div ref={cardRef}>
        <h2
          id="pomo-settings-title"
          style={{
            fontFamily: 'var(--ff-display, Fraunces), serif',
            fontStyle: 'italic',
            fontSize: 18,
            fontWeight: 500,
            color: 'var(--ink, #1c1a18)',
            margin: 0,
            marginBottom: 14,
          }}
        >
          Pomodoro · настройки
        </h2>

        <NumberField
          testId="pomo-set-work-min"
          label="Фокус (минут)"
          value={settings.workMin}
          min={1}
          max={120}
          onCommit={(v) => update({ workMin: clamp(v, 1, 120, 25) })}
        />
        <NumberField
          testId="pomo-set-short-break"
          label="Короткий перерыв (минут)"
          value={settings.shortBreakMin}
          min={1}
          max={60}
          onCommit={(v) => update({ shortBreakMin: clamp(v, 1, 60, 5) })}
        />
        <NumberField
          testId="pomo-set-long-break"
          label="Длинный перерыв (минут)"
          value={settings.longBreakMin}
          min={1}
          max={120}
          onCommit={(v) => update({ longBreakMin: clamp(v, 1, 120, 15) })}
        />
        <NumberField
          testId="pomo-set-long-break-after"
          label="Длинный перерыв после N циклов"
          value={settings.longBreakAfter}
          min={2}
          max={10}
          onCommit={(v) => update({ longBreakAfter: clamp(v, 2, 10, 4) })}
        />

        <CheckField
          testId="pomo-set-sound-on"
          label="Звуковой сигнал на конец фазы"
          checked={settings.soundOn}
          onChange={(v) => update({ soundOn: v })}
        />
        <CheckField
          testId="pomo-set-notif-on"
          label="Browser-уведомления (запрос разрешения при старте)"
          checked={settings.notifOn}
          onChange={(v) => update({ notifOn: v })}
        />
        <CheckField
          testId="pomo-set-auto-start"
          label="Авто-старт следующей фазы"
          checked={settings.autoStart}
          onChange={(v) => update({ autoStart: v })}
        />

        <label
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
            fontSize: 13,
            color: 'var(--ink-soft)',
          }}
        >
          <span>Пресет звука</span>
          <select
            data-testid="pomo-set-sound-preset"
            value={settings.soundPreset}
            onChange={(e) =>
              update({ soundPreset: e.target.value as PomoSettings['soundPreset'] })
            }
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 13,
              padding: '4px 8px',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--surface-2)',
              color: 'var(--ink)',
            }}
          >
            <option value="soft">Soft (низкий sine)</option>
            <option value="chime">Chime (sine 660 Hz)</option>
            <option value="bell">Bell (triangle 880 Hz)</option>
          </select>
        </label>

        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            className="cta-ghost"
            data-testid="pomo-set-reset-defaults"
            onClick={() => onResetDefaults()}
            style={{ fontSize: 11.5 }}
          >
            Сбросить настройки
          </button>
          <button
            type="button"
            className="cta-ghost"
            data-testid="pomo-set-reset-timer"
            onClick={() => onResetTimer()}
            style={{ fontSize: 11.5 }}
          >
            Сбросить таймер
          </button>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
            <button
              type="button"
              className="cta-ghost"
              data-testid="pomo-set-close"
              onClick={() => onClose()}
            >
              Закрыть
            </button>
            <button
              type="button"
              className="btn-primary"
              data-testid="pomo-set-save"
              onClick={() => onClose()}
            >
              Сохранить
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

interface NumberFieldProps {
  testId: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onCommit: (next: number) => void;
}

function NumberField({ testId, label, value, min, max, onCommit }: NumberFieldProps) {
  return (
    <label
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 0',
        fontSize: 13,
        color: 'var(--ink-soft)',
      }}
    >
      <span>{label}</span>
      <input
        type="number"
        data-testid={testId}
        min={min}
        max={max}
        value={value}
        onChange={(e) => onCommit(Number(e.target.value))}
        style={{
          width: 64,
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          padding: '4px 8px',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-sm)',
          background: 'var(--surface-2)',
          color: 'var(--ink)',
          textAlign: 'right',
        }}
      />
    </label>
  );
}

interface CheckFieldProps {
  testId: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}

function CheckField({ testId, label, checked, onChange }: CheckFieldProps) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 0',
        fontSize: 13,
        color: 'var(--ink-soft)',
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

/** Re-exported для widget convenience · matches PomoSettings hook export. */
export { DEFAULT_POMO_SETTINGS };
